import { GridColDef, GridSortModel, GridCellParams } from '@mui/x-data-grid';
import React, { useState, useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { DataGridModel, DataGrid } from 'src/shared/components/organisms/dataGrid';
import ConsoleHelper from 'src/shared/functions/consoleHelper';
import { useIsMounted } from 'src/shared/hooks/useIsMounted';
import { Info } from '@mui/icons-material';
import { getSmsByBatchId } from 'src/shared/services/smsService';
import { EmptyGridCell } from 'src/shared/components/atoms/emptyGridCell';
import { Typography } from '@mui/material';
import { GroupMessageSmsStatuses } from './groupMessageSmsStatuses';
import { SmsResponse } from '../models/smsResponse';
import { GroupMessageSmsStatusSearchFilter } from '../models/groupMessageSmsStatusSearchFilter';

type GroupMessageSmsStatusGridProps = {
  loadingCallback: (loading: boolean) => void;
  searchFilter: GroupMessageSmsStatusSearchFilter;
};

export const GroupMessageSmsStatusGrid = (props: GroupMessageSmsStatusGridProps) => {
  const { loadingCallback, searchFilter } = props;
  const { t } = useTranslation(['groupMessage']);
  const isMounted = useIsMounted();
  const [rows, setRows] = useState<SmsResponse[]>([]);
  const [data, setData] = React.useState<DataGridModel>({
    loading: true,
    totalRows: 0,
    rowsPerPageOptions: [5, 10, 15, 25, 50],
    pageSize: 25,
    page: 0,
  });

  const [sortModel, setSortModel] = useState<GridSortModel>([{ field: 'id', sort: 'desc' }]);

  const updateData = (k, v) => setData((prev) => ({ ...prev, [k]: v }));

  const onPageChange = (newPageIndex: number) => {
    updateData('page', newPageIndex);
  };

  const onPageSizeChange = (newPageSize: number) => {
    updateData('page', 0);
    updateData('pageSize', newPageSize);
  };

  ConsoleHelper.log(rows);

  const fetchData = useCallback(async () => {
    if (isMounted()) {
      updateData('loading', true);
      loadingCallback(true);
    }

    try {
      const result = await getSmsByBatchId(data.page, data.pageSize, sortModel[0].field, sortModel[0].sort, searchFilter);
      if (isMounted()) {
        const memberResult = result.data.results;
        updateData('totalRows', result.data.totalItems);

        setTimeout(() => {
          setRows(memberResult);
        }, 100);
      }
    } catch (e) {
      if (isMounted()) {
        ConsoleHelper.log(e);
      }
    } finally {
      if (isMounted()) {
        updateData('loading', false);
        loadingCallback(false);
      }
    }
  }, [isMounted, loadingCallback, data.page, data.pageSize, sortModel, searchFilter]);

  useEffect(() => {
    if (isMounted()) {
      fetchData();
    }
  }, [data.page, data.pageSize, fetchData, isMounted]);

  const columns: GridColDef[] = [
    {
      field: 'memberNumber',
      headerName: t('groupMessage:email:colHeads.memberNr'),
      sortable: true,
      flex: 0.5,
      renderCell: (params: GridCellParams) => {
        return <>{params.row.memberNumber || <EmptyGridCell />}</>;
      },
    },
    {
      field: 'memberName',
      headerName: t('groupMessage:tableHeaders.name'),
      sortable: true,
      flex: 2,
      renderCell: (params: GridCellParams) => {
        return (
          <>
            {params.row.memberName || (
              <Typography variant='body2' sx={{ color: (thm) => thm.palette.grey[500], fontStyle: 'italic' }}>
                {t('groupMessage:sms.copyTo')}
              </Typography>
            )}
          </>
        );
      },
    },
    {
      field: 'sendDate',
      headerName: t('groupMessage:email:colHeads.status'),
      sortable: true,
      flex: 1,
      renderCell: (params: GridCellParams) => {
        const { status } = params.row;
        return <GroupMessageSmsStatuses chipSize='small' status={status} />;
      },
    },
    {
      field: 'memberPhone',
      headerName: t('groupMessage:email:colHeads.phone'),
      sortable: true,
      flex: 1,
      renderCell: (params: GridCellParams) => {
        return <>{params.row.memberPhone}</>;
      },
    },
    {
      field: 'id',
      headerName: '',
      sortable: false,
      flex: 0.1,
      renderCell: () => {
        return (
          <>
            <Info fontSize='small' />
          </>
        );
      },
    },
  ];

  return (
    <DataGrid<SmsResponse>
      columns={columns}
      rows={rows}
      autoHeight
      columnVisibilityModel={{
        id: false,
      }}
      dataModel={data}
      checkboxSelection={false}
      disableSelectionOnClick
      onPageChange={onPageChange}
      onPageSizeChange={onPageSizeChange}
      sortModel={sortModel}
      onSortModelChange={(newSortModel) => {
        if (newSortModel.length) {
          setSortModel(newSortModel);
        } else {
          setSortModel([{ field: sortModel[0].field, sort: sortModel[0].sort === 'asc' ? 'desc' : 'asc' }]);
        }
      }}
      selectionModel={[]}
      onSelectionModelChange={() => ConsoleHelper.log('')}
    />
  );
};
